import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  Snackbar,
  SvgIcon,
  Typography,
} from '@mui/material'
import * as Sentry from '@sentry/react'
import axios from 'axios'
import { Form, Formik, FormikHelpers } from 'formik'
import React, { Suspense, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { APIError } from '../../types/interfaces'
import SubmitButton from '../components/forms/SubmitButtonWrapper'
import Textfield from '../components/forms/TextfieldWrapper'
import { Apple } from '../dummy/Apple'
import { Google } from '../dummy/Google'
import routes from '../router/routes'
import { signInSchema } from '../schema/input'
import { SignInFormType, SnackBarAlertType } from '../types/interface'
import ForcePassword from './ForcePassword'
import { useAuthContext } from './hooks/useAuthContext'

const ForgotPassword = React.lazy(() => import('./ForgotPassword'))

const SignIn = () => {
  const { handleAppleSSO, handleGoogleSSO, signIn, user } = useAuthContext()
  const { t } = useTranslation()
  const [alertEvent, setAlertEvent] = useState<SnackBarAlertType>({
    message: '',
    open: false,
    type: 'info',
  })
  const [open, setOpen] = useState<boolean>(false)
  const [showPasswordEvent, setShowPasswordEvent] = useState<boolean>(false)
  const [forcePasswordEvent, setForcePasswordEvent] = useState({
    email: '',
    password: '',
  })

  const initialValues: SignInFormType = {
    email: '',
    password: '',
  }

  const handleSignin = async (userValues: SignInFormType) => {
    const { nextStep } = await signIn({
      password: userValues.password,
      username: userValues.email,
    })

    if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      setForcePasswordEvent({
        email: userValues.email,
        password: userValues.password,
      })
      setShowPasswordEvent(true)
    } else {
      setAlertEvent({
        message: `${t('auth.signin.login_success')}`,
        open: true,
        type: 'success',
      })
    }
  }

  // Get translated error message from various error types
  const getTranslatedErrorMessage = (error: unknown): string => {
    const err = error as { code?: string; message?: string; status?: number }
    const errorMessage = (err.message || '').toLowerCase()

    // Firebase error codes
    if (err.code) {
      const codeMap: Record<string, string> = {
        'cancelled-popup-request': 'popup_closed',
        'invalid-credential': 'invalid_credentials',
        'invalid-email': 'invalid_email_format',
        'network-request-failed': 'network_error',
        'popup-closed-by-user': 'popup_closed',
        'too-many-requests': 'too_many_requests',
        'user-disabled': 'account_disabled',
        'user-not-found': 'invalid_credentials',
        'wrong-password': 'invalid_credentials',
      }
      
      const matchedKey = Object.keys(codeMap).find((key) => err.code?.includes(key))
      if (matchedKey) {
        return t(`auth.signin.errors.${codeMap[matchedKey]}`)
      }
    }

    // HTTP status codes
    if (err.status === 404 || err.status === 401) {
      return t('auth.signin.errors.invalid_credentials')
    }

    // Error message patterns
    const messagePatterns: Array<[RegExp, string]> = [
      [/invalid (email|password|credentials)/i, 'invalid_credentials'],
      [/invalid email format/i, 'invalid_email_format'],
      [/username and password are required/i, 'credentials_required'],
      [/authentication provider not supported/i, 'provider_not_supported'],
      [/unable to verify credentials/i, 'unable_to_verify'],
      [/network error/i, 'network_error'],
      [/too many/i, 'too_many_requests'],
      [/account.*disabled|disabled.*account/i, 'account_disabled'],
      [/popup.*(closed|cancelled)|(closed|cancelled).*popup/i, 'popup_closed'],
    ]

    const matchedPattern = messagePatterns.find(([pattern]) => pattern.test(errorMessage))
    if (matchedPattern) {
      return t(`auth.signin.errors.${matchedPattern[1]}`)
    }

    // Try to extract from axios/API errors
    if (axios.isAxiosError(error)) {
      const apiError = error.response?.data as APIError
      if (Array.isArray(apiError?.message)) {
        return apiError.message[0] || t('auth.signin.errors.generic')
      }
      if (apiError?.error || apiError?.message || error.message) {
        return apiError?.error || apiError?.message || error.message
      }
    }

    if (error instanceof Error && error.message) {
      return error.message
    }

    return t('auth.signin.errors.generic')
  }

  // Check if error is a user-initiated action (shouldn't log to Sentry)
  const isUserError = (error: unknown): boolean => {
    const err = error as { code?: string; message?: string; status?: number }
    return !!(
      err.code?.includes('popup-closed') ||
      err.code?.includes('cancelled-popup') ||
      err.code?.startsWith('auth/invalid') ||
      err.code === 'auth/user-not-found' ||
      err.code === 'auth/wrong-password' ||
      err.status === 404 ||
      err.status === 401
    )
  }

  // Handle SSO errors with consistent error handling
  const handleSSOError = (error: unknown) => {
    const errorMessage = getTranslatedErrorMessage(error)
    
    if (!isUserError(error)) {
      Sentry.captureException(error)
    }

    setAlertEvent({
      message: errorMessage,
      open: true,
      type: 'error',
    })
  }

  const onSubmit = async (
    userValues: SignInFormType,
    actions: FormikHelpers<SignInFormType>
  ) => {
    try {
      await handleSignin(userValues)
    } catch (error) {
      handleSSOError(error)
    } finally {
      actions.setSubmitting(false)
    }
  }

  const handleClose = (
    _event: Event | React.SyntheticEvent,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return
    }
    setAlertEvent({ ...alertEvent, open: false })
  }

  return (
    <>
      <Grid
        container
        sx={{
          p: 2,
        }}
      >
        <Grid item xs={12}>
          <Card>
            {user ? (
              <CardHeader
                title={
                  <Typography align="center" variant="h3">
                    {t('auth.signin.welcome')}
                  </Typography>
                }
              />
            ) : (
              <CardHeader
                subheader={
                  <Typography
                    align="center"
                    color="text.secondary"
                    sx={{ pt: 2 }}
                    variant="body2"
                  >
                    {t('auth.signin.description')}
                  </Typography>
                }
                title={
                  <Typography align="center" color="text.primary" variant="h4">
                    {t('auth.signin.welcome')}
                  </Typography>
                }
              />
            )}
            <CardContent>
              {user ? (
                <Grid container rowGap={2}>
                  <Grid item xs={12}>
                    <Button
                      component={Link}
                      fullWidth
                      size="large"
                      to={routes.portal.home}
                      variant="contained"
                    >
                      {t('auth.signin.cta_2')}
                    </Button>
                  </Grid>
                </Grid>
              ) : (
                <Grid container rowGap={2}>
                  <Grid item xs={12}>
                    <Grid container direction="column" rowGap={1}>
                      <Grid item xs={12}>
                        <Button
                          color="google"
                          disableElevation
                          fullWidth
                          onClick={async () => {
                            try {
                              await handleGoogleSSO()
                            } catch (error) {
                              handleSSOError(error)
                            }
                          }}
                          size="large"
                          startIcon={
                            <SvgIcon>
                              <Google />
                            </SvgIcon>
                          }
                          variant="outlined"
                        >
                          {t('auth.signin.sso')}
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          color="google"
                          disableElevation
                          fullWidth
                          onClick={async () => {
                            try {
                              await handleAppleSSO()
                            } catch (error) {
                              handleSSOError(error)
                            }
                          }}
                          size="large"
                          startIcon={
                            <SvgIcon>
                              <Apple />
                            </SvgIcon>
                          }
                          variant="outlined"
                        >
                          {t('auth.signin.apple_sso')}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider>
                      <Typography color="text.secondary" variant="subtitle2">
                        {t('auth.signin.option')}
                      </Typography>
                    </Divider>
                  </Grid>
                  <Grid item xs={12}>
                    <Formik
                      initialValues={initialValues}
                      onSubmit={onSubmit}
                      validationSchema={signInSchema}
                    >
                      {({ isSubmitting }) => (
                        <Form
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            rowGap: 16,
                          }}
                        >
                          <Textfield
                            label={t('auth.signin.email')}
                            name="email"
                            type="email"
                          />
                          <Textfield
                            label={t('auth.signin.password')}
                            name="password"
                            type="password"
                          />
                          <Grid
                            container
                            item
                            justifyContent="flex-end"
                            xs={12}
                          >
                            <Button
                              onClick={() => setOpen(true)}
                              size="small"
                              variant="text"
                            >
                              {t('auth.signin.forgot')}
                            </Button>
                          </Grid>
                          <SubmitButton
                            disabled={isSubmitting}
                            fullWidth
                            loading={isSubmitting}
                          >
                            {t('auth.signin.cta')}
                          </SubmitButton>
                        </Form>
                      )}
                    </Formik>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        autoHideDuration={6000}
        onClose={handleClose}
        open={alertEvent.open}
      >
        <Alert
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              onClick={handleClose}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          }
          elevation={2}
          onClose={handleClose}
          severity={alertEvent.type}
          sx={{
            width: '100%',
          }}
        >
          {alertEvent.message}
        </Alert>
      </Snackbar>
      <Suspense fallback={<>...</>}>
        <ForgotPassword open={open} setOpen={setOpen} />
      </Suspense>
      <ForcePassword
        open={showPasswordEvent}
        setOpen={setShowPasswordEvent}
        user={forcePasswordEvent}
      />
    </>
  )
}

export default SignIn
